import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { Resources } from '@/src/shared/constants/resources';
import { useSnackbar } from 'notistack';
import { useRedirect } from 'react-admin';
import { getFunctions, httpsCallable } from 'firebase/functions';
import ClubCreateForm from '@/src/pages/clubProfile/create/createSteps/ClubCreateForm';
import { Box, LinearProgress } from '@mui/material';
import Tutorial from '@/src/pages/clubProfile/create/createSteps/Tutorial';

type ClubCreateUserProps = {
  onClose: () => void;
};

export const ClubCreateUser = (props: ClubCreateUserProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const form = useForm({ mode: 'all' });
  const queryClient = useQueryClient();
  const redirect = useRedirect();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const { onClose } = props;

  async function onSubmit(data) {
    const club = data.club;

    if (!club) {
      enqueueSnackbar('Club details are required', { variant: 'error' });
      return;
    }

    setIsLoading(true);
    try {
      const instance = httpsCallable(getFunctions(), 'clubs-club-create');
      await instance(club);

      enqueueSnackbar('Club details saved successfully', { variant: 'success' });
      // Don't invalidate queries here - wait until the last step
      setStep(step + 1);
    } catch (e) {
      const errorMessage = e?.message || 'Failed to save club details';
      const isAlreadyExistsError = errorMessage.includes('already exists');

      if (isAlreadyExistsError) {
        // Don't invalidate queries here - wait until the last step
        setStep(step + 1);
      } else {
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }

  const handleFinalStep = () => {
    // Invalidate queries only on the last step before closing
    queryClient.invalidateQueries(Resources.Clubs);
    redirect('list', Resources.Billing);
    onClose();
  };

  const steps = [
    <ClubCreateForm isLoading={isLoading} onlyClubCreate={false} />,
    <Tutorial onContinue={handleFinalStep} />,
  ];

  const currentStep = step;
  const totalSteps = steps.length;

  return (
    <Box>
      <FormProvider {...form}>
        <form
          style={{
            minHeight: '514px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <LinearProgress
            sx={{ height: '10px', borderRadius: '8px', my: 4, mx: 12 }}
            variant="determinate"
            value={(currentStep / totalSteps) * 100}
          />
          {steps[currentStep - 1]}
        </form>
      </FormProvider>
    </Box>
  );
};

